import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastrService, ToastPackage } from 'ngx-toastr';
import { BaseToastComponent } from '@app/shared/toasts/base-toast.component';

interface HttpDebugToastData {
    method: string;
    url: string;
    durationMs: number;
    rawBytes?: number | null;
    wireBytes?: number | null;
    waitMs?: number | null;
}

@Component({
    selector: 'app-http-debug-toast',
    templateUrl: './http-debug-toast.component.html',
    styleUrls: ['./http-debug-toast.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HttpDebugToastComponent extends BaseToastComponent implements OnInit, OnDestroy {
    data: HttpDebugToastData;
    private timeoutId: ReturnType<typeof setTimeout> | null = null;
    private timeRemaining = 0;
    private lastStart = 0;

    constructor(
        protected toastrService: ToastrService,
        public toastPackage: ToastPackage,
        cdr: ChangeDetectorRef,
        ngZone: NgZone
    ) {
        super(toastrService, toastPackage, cdr, ngZone);
        this.data = JSON.parse(this.getToastMessage());
    }

    ngOnInit(): void {
        if (this.toastPackage.config.disableTimeOut) {
            return;
        }

        this.timeRemaining = this.toastPackage.config.timeOut || 0;
        if (this.timeRemaining > 0) {
            this.resumeTimer();
        }
    }

    @HostListener('mouseenter')
    onMouseEnter(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
            const elapsed = performance.now() - this.lastStart;
            this.timeRemaining = Math.max(0, this.timeRemaining - elapsed);
        }
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        if (this.timeRemaining > 0 && !this.toastPackage.config.disableTimeOut) {
            this.resumeTimer();
        }
    }

    ngOnDestroy(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    formatBytes(value?: number | null): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '—';
        }
        if (value < 1024) {
            return `${value} B`;
        }
        const kb = value / 1024;
        if (kb < 1024) {
            return `${kb.toFixed(1)} KB`;
        }
        const mb = kb / 1024;
        return `${mb.toFixed(1)} MB`;
    }

    get waitPercent(): number {
        const duration = this.safeDuration;
        if (!duration || this.data.waitMs === null || this.data.waitMs === undefined) {
            return 0;
        }
        const wait = Math.max(0, Math.min(this.data.waitMs, duration));
        return Math.round((wait / duration) * 100);
    }

    get loadMs(): number | null {
        const duration = this.safeDuration;
        if (!duration) {
            return null;
        }
        const wait = this.data.waitMs ?? 0;
        return Math.max(0, duration - wait);
    }

    get loadPercent(): number {
        const duration = this.safeDuration;
        if (!duration) {
            return 0;
        }
        return 100 - this.waitPercent;
    }

    private resumeTimer(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.lastStart = performance.now();
        this.timeoutId = setTimeout(() => {
            this.toastrService.clear(this.toastPackage.toastId);
        }, this.timeRemaining);
    }

    private get safeDuration(): number {
        const duration = this.data.durationMs ?? 0;
        if (!Number.isFinite(duration) || duration <= 0) {
            return 0;
        }
        return duration;
    }
}
